import { z } from "zod";

import { customerEntry } from "../customers/customers";
import { saleLineResponse } from "../sales/sales";
import { moneyText } from "../money";

/**
 * Keeping a phone's own copy up to date, so it can sell with no internet.
 *
 * The phone registers itself once, and is given its letter for receipt
 * numbers. After that it asks, every half minute or so, for whatever has
 * changed since it last asked: medicines, sizes and prices, and its branch's
 * batches. The very first time, it gets everything.
 */

/* --------------------------------------------------------------- devices */

export const registerDeviceRequest = z.object({
  /**
   * Made on the phone the first time it is used for this pharmacy, and kept.
   * Sent again on every launch; the server answers with the same letter.
   */
  id: z.string().uuid(),
  platform: z.enum(["android", "ios", "web"]),
  appVersion: z.string().trim().max(40).nullable().optional(),
});

export const deviceResponse = z.object({
  id: z.string().uuid(),
  /** "A", "B" … — in front of every receipt number this phone prints. */
  shortCode: z.string(),
  label: z.string(),
  /**
   * The highest receipt count the server has from this phone. A phone that
   * was signed out lost its own count; it carries on from here instead of
   * starting again at 1.
   */
  lastReceiptSeq: z.number().int(),
});

/* ----------------------------------------------------------------- check */

/**
 * "Anything new?" — asked before every pull. Each stamp changes whenever what
 * it covers has; the phone keeps the ones it last pulled with, and pulls only
 * when they differ. Nothing to pull is one read on the server.
 */
export const syncCheckQuery = z.object({
  branchId: z.string().uuid(),
});

export const syncCheckResponse = z.object({
  /** The server's clock, for telling a phone its own date is wrong. */
  serverTime: z.string(),
  /** This branch's copy: medicines, stock, sales, customers. Null: no copy for this person. */
  pull: z.string().nullable(),
  /** The report days of every branch this person sees. Null: no reports for this person. */
  reports: z.string().nullable(),
});

/* ------------------------------------------------------------------ pull */

export const syncPullQuery = z.object({
  branchId: z.string().uuid(),
  /**
   * `cursor` from the previous answer. Absent the first time, which returns
   * everything instead of what changed.
   */
  since: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time")
    .optional(),
});

export const copiedProduct = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameSo: z.string().nullable(),
  genericName: z.string().nullable(),
  strength: z.string().nullable(),
  form: z.string().nullable(),
  baseUnit: z.string(),
  categoryName: z.string().nullable(),
  isRx: z.boolean(),
  isActive: z.boolean(),
  /**
   * For the medicine's own page with no internet. Optional because a copy
   * saved before they were sent lacks them until its next full pull.
   */
  manufacturer: z.string().nullable().optional(),
  barcode: z.string().nullable().optional(),
  requiresExpiry: z.boolean().optional(),
  /** Gone for good: take it off the phone. */
  deleted: z.boolean(),
});

export const copiedSize = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  name: z.string(),
  multiplier: z.number().int(),
  sellPrice: moneyText,
  deleted: z.boolean(),
});

export const copiedBatch = z.object({
  id: z.string().uuid(),
  productId: z.string().uuid(),
  expiryDate: z.string().nullable(),
  status: z.enum(["active", "quarantined", "written_off"]),
  qtyBase: z.number().int(),
  oversold: z.boolean(),
  /** Null for somebody not allowed to see cost prices. */
  costPerBase: moneyText.nullable(),
  /** For the medicine's page and Expiry with no internet; optional as above. */
  batchNo: z.string().nullable().optional(),
  qtyReceivedBase: z.number().int().optional(),
  receivedAt: z.string().optional(),
  supplierName: z.string().nullable().optional(),
  deleted: z.boolean(),
});

/**
 * A sale as the phone keeps it: enough to list it, open it and send its
 * receipt again with no internet. Changing it still needs the server.
 */
export const syncedSale = z.object({
  id: z.string().uuid(),
  receiptNo: z.string(),
  status: z.enum(["completed", "voided"]),
  edited: z.boolean(),
  soldAt: z.string(),
  sellerName: z.string(),
  customerName: z.string().nullable(),
  customerPhone: z.string().nullable(),
  subtotal: moneyText,
  discount: moneyText,
  /** Taken off the whole sale, as an amount. Optional: older servers did not send it. */
  saleDiscount: moneyText.optional(),
  total: moneyText,
  lines: z.array(saleLineResponse),
  payments: z.array(z.object({ method: z.string(), amount: moneyText })),
  /** What the customer owed just after this sale, when some of it went on credit. */
  balanceAfter: moneyText.nullable().optional(),
});

/** A customer of this branch, for deyn with no internet. */
export const copiedCustomer = z.object({
  id: z.string().uuid(),
  name: z.string(),
  phone: z.string().nullable(),
  deleted: z.boolean(),
});

/** One line of a customer's account. Never changed once written, so only new ones are sent. */
export const copiedLedgerEntry = customerEntry.extend({ customerId: z.string().uuid() });

export const syncPullResponse = z.object({
  /** The server's clock, for telling a phone its own date is wrong. */
  serverTime: z.string(),
  /** Send back as `since` next time. */
  cursor: z.string(),
  /** Everything, not just changes: replace the copy rather than add to it. */
  full: z.boolean(),
  products: z.array(copiedProduct),
  sizes: z.array(copiedSize),
  batches: z.array(copiedBatch),
  /** The branch's most-sold medicines lately, most first. Always the whole list. */
  topProductIds: z.array(z.string().uuid()),
  /** For the top of a receipt made offline. */
  pharmacyName: z.string(),
  branchName: z.string(),
  /**
   * Under the name on a receipt: the branch's phone, or the pharmacy's when
   * the branch has none. Optional: older servers did not send it.
   */
  pharmacyPhone: z.string().nullable().optional(),
  /** This branch's sales from the last week that are new or changed. */
  sales: z.array(syncedSale),
  /** Sales to take off the phone: an old version replaced by an edit. */
  removedSaleIds: z.array(z.string().uuid()),
  /** This branch's customers, new or changed. Optional: servers before deyn did not send them. */
  customers: z.array(copiedCustomer).optional(),
  /** Their accounts' new lines. */
  ledger: z.array(copiedLedgerEntry).optional(),
});

export type RegisterDeviceRequest = z.infer<typeof registerDeviceRequest>;
export type DeviceResponse = z.infer<typeof deviceResponse>;
export type SyncCheckQuery = z.infer<typeof syncCheckQuery>;
export type SyncCheckResponse = z.infer<typeof syncCheckResponse>;
export type SyncPullQuery = z.infer<typeof syncPullQuery>;
export type CopiedProduct = z.infer<typeof copiedProduct>;
export type CopiedSize = z.infer<typeof copiedSize>;
export type CopiedBatchResponse = z.infer<typeof copiedBatch>;
export type SyncedSale = z.infer<typeof syncedSale>;
export type SyncPullResponse = z.infer<typeof syncPullResponse>;
export type CopiedCustomer = z.infer<typeof copiedCustomer>;
export type CopiedLedgerEntry = z.infer<typeof copiedLedgerEntry>;

/* --------------------------------------------------------- refused sales */

/**
 * A sale this phone made that the server refused, reported so the owners see
 * it. Sent again every run until it is dealt with; the answer says whether it
 * has been, so the phone can stop holding it.
 */
export const refusedKind = z.enum(["sale", "cancel", "edit"]);

export const reportRefusedSaleRequest = z.object({
  /** The sale's own id — or, for a cancel or an edit, the change's own. */
  id: z.string().uuid(),
  /**
   * A new sale, or a cancel or an edit made with no internet that the server
   * would not take — most often because another phone changed that sale first.
   */
  kind: refusedKind.optional(),
  /** For a cancel or an edit: the sale it was to change. */
  saleId: z.string().uuid().optional(),
  branchId: z.string().uuid(),
  deviceId: z.string().uuid(),
  receiptNo: z.string().max(20),
  soldAt: z
    .string()
    .max(40)
    .refine((v) => !Number.isNaN(Date.parse(v)), "Not a time"),
  total: moneyText,
  /** The server's error code when it refused it. */
  errorCode: z.string().max(80),
  /** The sale exactly as it was sent. */
  sale: z.record(z.string(), z.unknown()),
});

export const reportRefusedSaleResponse = z.object({
  /** Null while it waits on the owners' list. */
  resolution: z.enum(["recorded", "removed"]).nullable(),
});

/* ------------------------------------------------------- needs attention */

/**
 * The owners' "Needs attention" list for one branch: sales the server
 * refused, medicines below zero, and expired stock sold in the last week.
 */
export const attentionQuery = z.object({ branchId: z.string().uuid() });

export const attentionResponse = z.object({
  refused: z.array(
    z.object({
      id: z.string().uuid(),
      /** A sale not recorded, or a cancel or an edit not made. */
      kind: refusedKind,
      receiptNo: z.string(),
      sellerName: z.string(),
      soldAt: z.string(),
      total: moneyText,
      errorCode: z.string(),
    }),
  ),
  belowZero: z.array(
    z.object({
      productId: z.string().uuid(),
      name: z.string(),
      strength: z.string().nullable(),
      baseUnit: z.string(),
      /** Below zero, in the smallest unit. */
      qtyBase: z.number().int(),
    }),
  ),
  expiredSold: z.array(
    z.object({
      saleId: z.string().uuid(),
      receiptNo: z.string(),
      soldAt: z.string(),
      productName: z.string(),
      baseUnit: z.string(),
      /** How much of it came from expired stock, in the smallest unit. */
      qtyBase: z.number().int(),
      expiryDate: z.string(),
    }),
  ),
});

export type ReportRefusedSaleRequest = z.infer<typeof reportRefusedSaleRequest>;
export type ReportRefusedSaleResponse = z.infer<typeof reportRefusedSaleResponse>;
export type AttentionQuery = z.infer<typeof attentionQuery>;
export type AttentionResponse = z.infer<typeof attentionResponse>;
